/**
 * IMPORTS
 */
import conversationsSelectors from 'src/aggregates/conversations/selectors';
import {fetchContactsOngoing} from 'src/aggregates/conversations/utils';


/**
 * TYPES
 */
import {IOngoingConversation} from 'src/aggregates/conversations/queries.d';
import {IConversation} from 'src/aggregates/conversations/state.d';


/**
 * CODE
 */

/**
 * I find the ongoing conversation of a contact sibling on the server.
 *
 * :param account: account id
 * :param siblings: uids of the contact siblings
 *
 * :returns: promise with the ongoing conversation of the first sibling
 *           talking on the account, with any agent, or null
 */
async function findSiblingConversation (
    account: number,
    siblings: string[],
): Promise<IOngoingConversation | null>
{
    // no siblings: return null
    if (siblings.length === 0)
    {
        return null;
    }

    // fetch the ongoing conversations of the siblings on the company
    const ongoing = await fetchContactsOngoing(siblings);

    // fetch failed: return no sibling conversation
    if (ongoing === null)
    {
        return null;
    }

    // return the first sibling conversation on the account, or null
    return ongoing.find(
        conversation => Number(conversation.account.id) === account,
    ) ?? null;
}


/**
 * I find the store conversation the account holds with a contact sibling.
 *
 * :param account: account id
 * :param siblings: uids of the contact siblings
 *
 * :returns: conversation of the first sibling talking on the account, or null
 */
function findSiblingConversationOnStore (
    account: number,
    siblings: string[],
): IConversation | null
{
    // look for a conversation on each sibling
    for (const sibling of siblings)
    {
        const conversation = conversationsSelectors
            .getConversationByAccountAndUid(account, sibling);

        // sibling has an ongoing conversation: return it
        if (conversation !== null)
        {
            return conversation;
        }
    }

    // no sibling is talking: return null
    return null;
}


/**
 * EXPORTS
 */
export {
    findSiblingConversation,
    findSiblingConversationOnStore,
};
